/**
 * Chat theming: turns a channel's branding into CSS custom properties.
 *
 * Two rules this module exists to enforce:
 *
 * 1. Text on a bubble is chosen by *measured* contrast, never by a lightness
 *    threshold. The old threshold picked white on Grasp cyan (2.67:1, below the
 *    4.5:1 AA minimum); dark ink on that same cyan is 5.95:1.
 * 2. A channel can never configure an unreadable chat. If neither text colour
 *    clears AA on the supplied fill, the fill itself is nudged until one does.
 */

// Grasp brand constants. Indigo carries white at 8.43:1, so it is the default
// learner bubble; cyan is reserved for interactive accents.
const GRASP_INDIGO = '#34459e';
const GRASP_CYAN = '#00acd5';
const INK = '#1a2233';
const WHITE = '#ffffff';

const AA_CONTRAST = 4.5;

/**
 * Production stores colour *names* rather than hex in
 * webchat_channel.primary_color ("green", "Cool Gray", "Magenta"), so parsing
 * hex alone resolves nothing and every channel falls back to the same blue.
 * Mapping the observed vocabulary makes existing data work; storing hex
 * upstream is the real fix.
 */
const NAMED_COLORS = {
  black: '#1a2233',
  blue: '#34459e',
  cyan: '#00acd5',
  'cool gray': '#5a6478',
  'cool grey': '#5a6478',
  gold: '#c8a24a',
  gray: '#545e78',
  green: '#2f9660',
  grey: '#545e78',
  magenta: '#a4368c',
  maroon: '#8c2f3f',
  navy: '#232f73',
  orange: '#c2661d',
  pink: '#c2538b',
  purple: '#6b4ea8',
  red: '#c73545',
  teal: '#128b8b',
  turquoise: '#12a4a9',
  violet: '#6b4ea8',
  white: '#ffffff',
  yellow: '#c8a24a',
};

export function parseHex(color) {
  if (!color || typeof color !== 'string') return null;
  let hex = color.trim().toLowerCase();
  if (NAMED_COLORS[hex]) hex = NAMED_COLORS[hex];
  if (hex.startsWith('#')) hex = hex.slice(1);
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-f]{6}$/.test(hex)) return null;
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
    hex: `#${hex}`,
  };
}

function relativeLuminance({ r, g, b }) {
  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG 2.1 contrast ratio between two parsed colours. 1:1 to 21:1. */
export function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function blend(color, target, amount) {
  return {
    r: Math.round(color.r + (target.r - color.r) * amount),
    g: Math.round(color.g + (target.g - color.g) * amount),
    b: Math.round(color.b + (target.b - color.b) * amount),
  };
}

function toHex({ r, g, b }) {
  const part = (v) =>
    Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

/** Whichever of ink/white reads better on this background. */
export function contrastTextColor(background) {
  const parsed = typeof background === 'string' ? parseHex(background) : background;
  if (!parsed) return WHITE;
  const vsInk = contrastRatio(parsed, parseHex(INK));
  const vsWhite = contrastRatio(parsed, parseHex(WHITE));
  return vsInk >= vsWhite ? INK : WHITE;
}

/**
 * Resolve a fill that is guaranteed to clear AA against one of the two text
 * colours. If the raw colour already passes it is returned untouched; otherwise
 * we walk it toward black (which helps white text) and toward white (which
 * helps ink) and take whichever converges in fewer steps.
 *
 * @returns {{fill: string, text: string, ratio: number, adjusted: boolean}}
 */
export function resolveAccessibleFill(candidate, fallback = GRASP_INDIGO) {
  const bg = parseHex(candidate);
  if (!bg) {
    const fb = parseHex(fallback) || parseHex(GRASP_INDIGO);
    const text = contrastTextColor(fb);
    return {
      fill: fb.hex,
      text,
      ratio: contrastRatio(fb, parseHex(text)),
      adjusted: false,
    };
  }

  const directText = contrastTextColor(bg);
  const directRatio = contrastRatio(bg, parseHex(directText));
  if (directRatio >= AA_CONTRAST) {
    return { fill: bg.hex, text: directText, ratio: directRatio, adjusted: false };
  }

  const black = { r: 0, g: 0, b: 0 };
  const white = { r: 255, g: 255, b: 255 };
  let darkSteps = -1;
  let lightSteps = -1;
  let darkened = null;
  let lightened = null;

  for (let i = 1; i <= 20; i += 1) {
    const amount = i * 0.05;
    if (darkSteps < 0) {
      const d = blend(bg, black, amount);
      if (contrastRatio(d, parseHex(WHITE)) >= AA_CONTRAST) {
        darkSteps = i;
        darkened = d;
      }
    }
    if (lightSteps < 0) {
      const l = blend(bg, white, amount);
      if (contrastRatio(l, parseHex(INK)) >= AA_CONTRAST) {
        lightSteps = i;
        lightened = l;
      }
    }
  }

  if (darkSteps >= 0 && (lightSteps < 0 || darkSteps <= lightSteps)) {
    return {
      fill: toHex(darkened),
      text: WHITE,
      ratio: contrastRatio(darkened, parseHex(WHITE)),
      adjusted: true,
    };
  }
  if (lightSteps >= 0) {
    return {
      fill: toHex(lightened),
      text: INK,
      ratio: contrastRatio(lightened, parseHex(INK)),
      adjusted: true,
    };
  }

  const fb = parseHex(fallback) || parseHex(GRASP_INDIGO);
  const text = contrastTextColor(fb);
  return {
    fill: fb.hex,
    text,
    ratio: contrastRatio(fb, parseHex(text)),
    adjusted: true,
  };
}

/**
 * Resolve an accent usable as a solid fill (send button, answer keys).
 *
 * Deliberately *not* held to a 3:1 boundary ratio: Grasp's own cyan is 2.67:1
 * against white, and a filled control with AA-compliant label text is fine
 * under WCAG 1.4.11. The only case worth rejecting is a near-white value, which
 * would render the control invisible on a light dock.
 */
const ACCENT_MAX_LUMINANCE = 0.82;

export function resolveAccentColor(candidate) {
  const parsed = parseHex(candidate);
  if (!parsed) return GRASP_CYAN;
  if (relativeLuminance(parsed) > ACCENT_MAX_LUMINANCE) return GRASP_CYAN;
  return parsed.hex;
}

/**
 * Build the chat theme for a channel.
 *
 * `secondary_color` drives the learner bubble (it is the channel's louder
 * colour in the existing data) and `primary_color` drives interactive accents,
 * each falling back to the other and finally to the Grasp defaults.
 */
export function buildChatTheme(channel) {
  const bubble = resolveAccessibleFill(
    channel?.secondary_color || channel?.primary_color,
    GRASP_INDIGO
  );

  const accent = resolveAccentColor(
    channel?.primary_color || channel?.secondary_color
  );
  const accentText = contrastTextColor(accent);

  // The header hairline. A channel with colours of its own gets a two-tone
  // line in them; one without keeps the Grasp spectrum (the CSS fallback).
  const branded = Boolean(channel?.primary_color || channel?.secondary_color);
  const brandLine = branded
    ? `linear-gradient(90deg, ${accent} 0 50%, ${bubble.fill} 50% 100%)`
    : '';

  return {
    accent,
    accentText,
    userBubbleBg: bubble.fill,
    userBubbleText: bubble.text,
    userBubbleRatio: bubble.ratio,
    userBubbleAdjusted: bubble.adjusted,
    brandLine,
    cssVars: {
      '--chat-user-bg': bubble.fill,
      '--chat-user-text': bubble.text,
      '--chat-accent': accent,
      '--chat-accent-text': accentText,
      '--chat-brand-line': brandLine,
    },
  };
}

/**
 * Write the theme onto :root.
 *
 * The old code set these on .chat-shell, which left the page backdrop outside
 * the themed subtree and meant portalled surfaces (the video theater renders
 * into document.body) escaped theming entirely. One root fixes both.
 */
export function applyChatTheme(theme) {
  if (typeof document === 'undefined' || !theme?.cssVars) return;
  const root = document.documentElement;
  Object.entries(theme.cssVars).forEach(([name, value]) => {
    // An empty value must clear the property, or a previous channel's line
    // would survive navigation to a channel without colours.
    if (value) root.style.setProperty(name, value);
    else root.style.removeProperty(name);
  });
}

export function bubbleRadiusClass(isFromUser, isFirstInGroup, isLastInGroup) {
  if (isFromUser) {
    if (isFirstInGroup && isLastInGroup) return 'rounded-2xl rounded-br-md';
    if (isFirstInGroup) return 'rounded-2xl rounded-br-md rounded-tr-2xl';
    if (isLastInGroup) return 'rounded-2xl rounded-tr-md rounded-br-md';
    return 'rounded-2xl rounded-r-md';
  }
  if (isFirstInGroup && isLastInGroup) return 'rounded-2xl rounded-bl-md';
  if (isFirstInGroup) return 'rounded-2xl rounded-bl-md rounded-tl-2xl';
  if (isLastInGroup) return 'rounded-2xl rounded-tl-md rounded-bl-md';
  return 'rounded-2xl rounded-l-md';
}
